import {
  Lit3formBlock,
  html,
  unsafeHTML,
  classMap,
} from '@Root/lib/lit-3form-block';
import { styles } from './css';
import { analyticsLabelFromString } from '../../../helpers/adobe-analytics';

export class Rose extends Lit3formBlock {
  static get characterName() {
    return 'rose';
  }

  static get characterProperties() {
    return {
      header: { type: String },
      description: { type: String },
      cta: { type: String },
      requiresScrollOnly: { type: Boolean },
      showHeaderRow: { type: Boolean },
      imagesLoaded: { type: Boolean },
      scrollBarPadding: { type: String },
    };
  }

  static get characterStyles() {
    return [...styles];
  }

  constructor() {
    super();
    this.imagesLoaded = 0;
    this.scrollBarPadding = '';
    this.requiresScrollOnly = false;
    this.showHeaderRow = false;
  }

  render() {
    return html`
      <div class="block-wrapper">
        <div id="rose-block" class="${classMap(this.wrapperClasses)}">
          ${this.requiresScrollOnly ? this.scrollBtnsHTML : html``}
          ${this.showHeaderRow
            ? html`
                <h2 class="header">${unsafeHTML(this.headerText)}</h2>
                <p class="description">${unsafeHTML(this.descriptionText)}</p>
                ${this._requiresHorizontalScroll ? this.scrollBtnsHTML : html``}
              `
            : html``}
          <div id="rose-images" class="images" style="${this.scrollBarPadding}">
            ${this.images.map(
              image =>
                html`
                  <div class="image">
                    <img
                      src=${this.imagebossUrl(image[1].src, null, '400')}
                      decoding="async"
                      @load="${this.imgLoaded}"
                    />
                    <div class="image-caption">
                      <span>
                        <p>${unsafeHTML(this.imageDescription(image[1]))}</p>
                      </span>
                      <span>
                        <download-button
                          downloadData=${JSON.stringify({
                            file_format: 'JPG',
                            file_name: analyticsLabelFromString(
                              this.imageDescription(image[1])
                            ),
                            link_url: image[1].full_resolution_src,
                            link_type: 'IMAGE',
                            placement: analyticsLabelFromString(
                              this.adobePlacementString(image)
                            ),
                          })}
                        >
                        </download-button>
                      </span>
                    </div>
                  </div>
                `
            )}
          </div>
          ${this.horizontalRule}
        </div>
      </div>
    `;
  }

  get wrapperClasses() {
    this.showHeaderRow = this._requiresHeaderRow;
    const classObject = {
      'rose-layout': true,
    };
    classObject['include-header'] = this.showHeaderRow;
    classObject['exclude-header'] = !this.showHeaderRow;
    classObject['elements-colors'] = this.elementsColors;

    return classObject;
  }

  get scrollBtnsHTML() {
    return html`
      <span class="scroll-buttons">
        <span @click="${() => this.sideScroll('left')}" class="left-arrow">
          <img src="/static/icons/SVG/arrow-black-5x10.svg" />
        </span>
        <span @click="${() => this.sideScroll('right')}" class="right-arrow">
          <img src="/static/icons/SVG/arrow-black-5x10.svg" />
        </span>
      </span>
    `;
  }

  imgLoaded() {
    this.imagesLoaded++;
    if (this.imagesLoaded === this.images.length) {
      if (this._requiresHorizontalScroll)
        this.scrollBarPadding =
          'padding-bottom: calc(30em / var(--default-rem-px-count));';

      if (this._requiresHorizontalScroll && !this._requiresHeaderRow) {
        this.requiresScrollOnly = true;
      } else if (this._requiresHeaderRow) {
        this.showHeaderRow = true;
      }

      this.requestUpdate();
    }
  }

  sideScroll(direction) {
    const element = this.shadowRoot.getElementById('rose-images');
    let scrollAmount = 0;
    const step = 40;
    const distance = 700;
    const slideTimer = setInterval(function() {
      direction === 'left'
        ? (element.scrollLeft -= step)
        : (element.scrollLeft += step);
      scrollAmount += step;
      if (scrollAmount >= distance) {
        window.clearInterval(slideTimer);
      }
    }, 10);
  }

  get _roseBlockWidth() {
    /*
      This is hard-coded to 34 for now because it's based on column
      width specifically at the 1200px screen cutoff (32rem + 2rem for the
      outside columns).
    */
    return this.shadowRoot.getElementById('rose-block').scrollWidth - 34;
  }

  get _roseImagesWidth() {
    return this.shadowRoot.getElementById('rose-images')?.scrollWidth || null;
  }

  get _requiresHeaderRow() {
    return !!this.headerText || !!this.descriptionText;
  }

  get headerText() {
    return this.withLineBreaks(this.content.header) || '';
  }

  get descriptionText() {
    return this.withLineBreaks(this.content.description) || '';
  }

  get _requiresHorizontalScroll() {
    if (!this._roseImagesWidth) {
      // default use of horizontal scroll arrows to true
      return true;
    } else {
      return this._roseImagesWidth > this._roseBlockWidth;
    }
  }

  get images() {
    return this.content.images;
  }

  get elementsColors() {
    return this.content.elements_colors && this.content.elements_colors == '1';
  }

  imageDescription(image) {
    return this.withLineBreaks(image.description);
  }

  adobePlacementString(image) {
    const placementStr =
      this.headerText || this.imageDescription(image) || 'Rose Block';
    return placementStr;
  }
}
Rose.defineElement();
